import { BOPS_NAME } from '@utils/constants';
import getCSLCookie from '@modules/shop-your-store/composables/getCSLCookie';

// Extrct key:value from URL
export function extractKeyValuesFromFilterPath(url) {
  /* istanbul ignore else */
  if (!url) {
    return [];
  }

  /* */
  const noStartingSlash = url.startsWith('/') ? url.slice(1) : url;
  const [keysString, valuesString] = noStartingSlash.split('/');
  const keys = keysString.split(',');
  const values = valuesString.split(/,(?! )/); // key values can be '1,2,3, S, 3.1, S,4,5' => ['1', '2', '3, S, 3.1, S', '4', '5']
  const result = [];

  keys.forEach((key, index) => {
    const keyValueObject = {
      key,
      value: values[index],
    };
    result.push(keyValueObject);
  });
  return result;
}

// Sort filters & navigation
export function sortByProperty(prop) {
  return (a, b) => {
    const propA = a[prop].toUpperCase();
    const propB = b[prop].toUpperCase();
    return ((propA < propB) && -1) || ((propA > propB) && 1) || 0;
  };
}

// Create pathname string from pagination & filters
export function combineArraysToString(paginationItems, filtersItems) {
  /* istanbul ignore else */
  if (!paginationItems.length && !filtersItems.length) {
    return '';
  }
  const keysArray = [];
  const valuesArray = [];

  const sortByKey = sortByProperty('key');

  paginationItems.sort(sortByKey);

  filtersItems.forEach((obj) => {
    const { key, value } = obj;
    keysArray.push(key);
    valuesArray.push(value);
  });

  paginationItems.forEach((obj) => {
    const { key, value } = obj;
    keysArray.push(key);
    valuesArray.push(value);
  });

  const keysString = keysArray.join(',');
  const valuesString = valuesArray.join(',');
  const finalString = `${keysString}/${valuesString}`;

  return finalString;
}

function removeHash(urlQuery = '') {
  return urlQuery.split('#');
}

export function getQuery(urlQuery = '', { selectedNames, selectedValuesList } = {}, fullPath = '') {
  let query = urlQuery;
  const [urlNoHash, hash] = removeHash(urlQuery);
  const params = new URLSearchParams(urlNoHash);
  /* istanbul ignore else */
  if (selectedNames && selectedValuesList) {
    const bopsLocationIndex = selectedNames.findIndex((name) => name.toLowerCase() === BOPS_NAME.toLowerCase());

    /* istanbul ignore else */
    if (bopsLocationIndex !== -1) {
      // add query based on bops presence
      params.set('_additionalStoreLocations', selectedValuesList[bopsLocationIndex]);
      query = params.toString();
    } else if (params.has('_additionalStoreLocations')) {
      params.delete('_additionalStoreLocations');
      query = params.toString();
    }
  }
  /* istanbul ignore else */
  if (
    params.has('_ffSearchQuery')
    && fullPath?.includes(BOPS_NAME)
  ) {
    /* istanbul ignore else */
    if (params.has('_additionalStoreLocations')) {
      params.delete('_additionalStoreLocations');
    }
    const csl = getCSLCookie();
    params.append('_additionalStoreLocations', csl);
    query = params.toString();
  }
  /* istanbul ignore else */
  if (hash && !query.includes('#')) {
    query += `#${hash}`;
  }

  return query;
}

export default function generateUrlPathUtil(args) {
  // Pagination & Filters
  let filtersItems = [];

  const {
    fullPath, facetsParams, paginationData, isPagination, selectedFacetUrl, metaBaseUrl, removePageIndex = false,
  } = args;

  // Get path & queries from URL
  // eslint-disable-next-line no-unused-vars
  const { 0: head, 1: urlQuery } = fullPath.split('?');
  let pathName = metaBaseUrl;
  const query = getQuery(urlQuery, facetsParams, fullPath);

  // Extract key:value pairs from selectedFacetUrl
  const keyValues = extractKeyValuesFromFilterPath(selectedFacetUrl);

  // Save initial filters key:value pairs
  /* istanbul ignore else */
  if (keyValues.length > 0) {
    keyValues.forEach(({ key, value }) => {
      /* istanbul ignore else */
      if (!['Sortby', 'Productsperpage', 'Pageindex'].includes(key)) {
        filtersItems.push({ key, value });
      }
    });
  }

  // Handle facets
  /* istanbul ignore else */
  if (facetsParams && !isPagination) {
    filtersItems = [];
    facetsParams.selectedNames.forEach((key, index) => {
      const value = facetsParams.selectedValuesList[index];
      const pageIndex = paginationData.findIndex((item) => item.key === 'Pageindex');
      if (!['Sortby', 'Productsperpage', 'Pageindex'].includes(key)) {
        filtersItems.push({ key, value });
        /* istanbul ignore else */
        if (pageIndex !== -1) {
          paginationData.splice(pageIndex, 1);
        }
      } else {
        // Check if key is present in paginationData and update its value
        const paginationIndex = paginationData.findIndex((item) => item.key === key);
        if (paginationIndex !== -1) {
          paginationData[paginationIndex].value = value;
        } else {
          // If not present, add it to paginationData
          paginationData.push({ key, value });
        }

        // If the key is 'Sortby', also reset 'Pageindex' to 1
        /* istanbul ignore else */
        if (key === 'Sortby') {
          /* istanbul ignore else */
          if (pageIndex !== -1) {
            paginationData.splice(pageIndex, 1);
          }
        }
      }
    });
  }
  // Remove 'ORIGINAL' from the URL
  let paginationItems = paginationData?.filter((item) => item.value !== 'ORIGINAL') || [];
  /* istanbul ignore else */
  if (removePageIndex) {
    paginationItems = paginationData?.filter((item) => item.key !== 'Pageindex') || [];
  }

  // Create path string from pagination & filters
  const pathString = combineArraysToString(paginationItems, filtersItems);
  /* istanbul ignore else */
  if (pathString) {
    pathName = `${pathName.replace(/\/$/, '')}/${pathString}`;
  }

  return query ? `${pathName}?${query}` : pathName;
}
